/** Shared recruiting taxonomy. Crosswalks are orientation, not certification or equivalence. */
export const capabilityModelVersion = "ot-capability-v2";
export const capabilityLevels = [
    "Awareness",
    "Assisted",
    "Independent",
    "Leads delivery",
    "Defines strategy",
];
export const capabilityScale = "1 Awareness · 2 Assisted · 3 Independent · 4 Leads delivery · 5 Defines strategy";
export const capabilityDomains = [
    {
        id: "industrial",
        label: "Industrial systems and safety",
        shortLabel: "Industrial systems",
        csf: "Identify / Protect",
        iec: "62443-2-1 / 3-3 resource availability",
        pattern: "OT|ICS|SCADA|DCS|PLCs?|HMIs?|industrial (?:controls?|automation|systems?)|operational technology|process (?:control|safety)|functional safety|manufacturing (?:systems?|technology)|control systems?|ladder logic|Modbus|OPC(?: UA)?|SIS",
    },
    {
        id: "network",
        label: "Network security and segmentation",
        shortLabel: "Network security",
        csf: "Protect",
        iec: "62443-3-2 zones/conduits; 3-3 restricted data flow",
        pattern: "network(?:ing|s| security| design| architecture| segmentation)?|segmentation|zones? and conduits?|firewalls?|Purdue|DMZ|VLANs?|TCP/IP|routing|switching|microsegmentation",
    },
    {
        id: "access",
        label: "Identity, access and remote connectivity",
        shortLabel: "Identity & access",
        csf: "Protect",
        iec: "62443-3-3 identification/authentication and use control",
        pattern: "IAM|PAM|identity|authentication|authorization|access control|remote access|privileged access|least privilege|zero trust|MFA|VPNs?|active directory",
    },
    {
        id: "assets",
        label: "Asset and vulnerability management",
        shortLabel: "Assets & vulnerabilities",
        csf: "Identify / Protect",
        iec: "62443-2-1 asset management; 2-3 patch management",
        pattern: "asset (?:inventory|inventories|visibility|discovery|management)|inventor(?:y|ies)|vulnerabilit(?:y|ies)|patch(?:ing| management)?|exposure|hardening|configuration management|CVE|passive discovery|Claroty|Nozomi|Tenable",
    },
    {
        id: "detection",
        label: "Detection and security operations",
        shortLabel: "Detection",
        csf: "Detect",
        iec: "62443-3-3 timely response to events",
        pattern: "detect(?:ion|ions|ing)?|monitor(?:ing)?|SIEM|SOC|security operations|threat (?:hunting|intelligence)|alerts?|telemetry|logging|IDS|IPS|Dragos|Splunk",
    },
    {
        id: "response",
        label: "Incident response and recovery",
        shortLabel: "Response & recovery",
        csf: "Respond / Recover",
        iec: "62443-2-1 incident management; 3-3 resource availability",
        pattern: "incident (?:response|handling|management)|containment|triage|recover(?:y|ing)?|forensics?|tabletop|response exercise|disaster recovery|business continuity|backup|restore|restoration",
    },
    {
        id: "architecture",
        label: "Security architecture and engineering",
        shortLabel: "Architecture",
        csf: "Identify / Protect",
        iec: "62443-3-2 risk-based system design; 3-3 system requirements",
        pattern: "architect(?:ure|ures|s)?|architectural|security engineering|62443|ISA[ -]?99|800[ -]82|secure (?:design|development)|defen[cs]e.in.depth|threat model(?:ing)?|security (?:design|requirements)|system integrat(?:ion|or)",
    },
    {
        id: "governance",
        label: "Risk, governance and program leadership",
        shortLabel: "Risk & governance",
        csf: "Govern / Identify",
        iec: "62443-2-1 security program management",
        pattern: "governance|governed|risk (?:assessment|review|management)|compliance|audit(?:s|ing)?|polic(?:y|ies)|NERC[ -]?CIP|roadmap|program(?:me)?|strategy|strategic|stakeholder|cross[ -]functional|leadership|regulatory",
    },
];
/** Conservative, explainable suggestions from each clause, never a title-wide rating. */
export function extractCapabilitySignals(text) {
    const clauses = text
        .replace(/<[^>]*>/gu, " ")
        .split(/(?:[\n;•]+|[.!?]\s+|\s+(?:but|whereas|however)\s+|,\s+(?=(?:led|lead|assist|support|define|design|implement|build|manage)\b))/iu)
        .map((part) => part.trim())
        .filter(Boolean);
    return capabilityDomains.map((domain) => {
        const matcher = new RegExp(`\\b(?:${domain.pattern})\\b`, "iu");
        const evidence = [...new Set(clauses.filter((part) => matcher.test(part)))].filter((part) => !/\b(?:no|without|lack(?:s|ing)?)\s+(?:\w+\s+){0,3}(?:experience|knowledge|exposure|responsibility|involvement)\b/iu.test(part.slice(0, part.search(matcher))) && !/\b(?:not required|no experience|not experienced|not responsible)\b/iu.test(part));
        const scope = (part) => {
            if (/\b(?:assist(?:ed|ing)?|support(?:ed|ing)?|under (?:supervision|guidance))\b/iu.test(part))
                return 2;
            if (/\b(?:awareness|familiar(?:ity)?|basic|exposure|understand(?:ing)?)\b/iu.test(part))
                return 1;
            if (/\b(?:defin(?:e|ed|es|ing)|establish(?:ed|es|ing)?|set(?:s|ting)?)\b.{0,65}\b(?:strategy|strategic direction|enterprise standards|enterprise policy)\b/iu.test(part))
                return 5;
            if (/\b(?:led|lead(?:s|ing)?|direct(?:ed|ing)?|overs(?:ee|aw)|govern(?:ed|s)?|own(?:ed|s)?|accountable)\b/iu.test(part))
                return 4;
            if (/\b(?:manag(?:e|ed|es|ing)|independent(?:ly)?|hands.on|design(?:ed|s|ing)?|implement(?:ed|s|ing)?|build|built|develop(?:ed|s|ing)?|deploy(?:ed|s|ing)?|operat(?:e|ed|es|ing)|configur(?:e|ed|es|ing)|engineer(?:ed|ing)|deliver(?:ed|s|ing)?|maintain(?:ed|s|ing)?|perform(?:ed|s|ing)?|conduct(?:ed|s|ing)?)\b/iu.test(part))
                return 3;
            return null;
        };
        return {
            id: domain.id,
            label: domain.label,
            shortLabel: domain.shortLabel,
            level: evidence.some((part) => scope(part) !== null)
                ? Math.max(...evidence.map((part) => scope(part) ?? 0))
                : null,
            evidence: evidence.sort((a, b) => (scope(b) ?? 0) - (scope(a) ?? 0)).slice(0, 3),
        };
    });
}
